import React from 'react'
import validator from 'validator'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { firebaseConnect } from 'react-redux-firebase'
import { reduxForm, Field, SubmissionError } from 'redux-form'
import { routerActions } from 'react-router-redux'
import { FormattedMessage, defineMessages, intlShape } from 'react-intl'
import { isValidAddress, isValidChecksumAddress } from 'ethereumjs-util'

import * as UI from '../components'
import * as commands from '../commands'
import * as constants from '../constants'

const walletOptions = constants.walletOptions

const messages = defineMessages({
  header: {
    id: 'sign-up.header',
    defaultMessage:
      'Registering will speed up your purchase process on the sale day. Do it.',
  },
  haveAnAccount: {
    id: 'sign-up.have-an-account',
    defaultMessage: 'Already have an account?',
  },
  signIn: {
    id: 'log-in.sign-in-message',
    defaultMessage: 'Click here to sign in!',
  },
  displayNameLabel: { id: 'sign-up.displayName.label', defaultMessage: 'Name' },
  emailLabel: { id: 'sign-up.email.label', defaultMessage: 'E-mail Address' },
  passwordLabel: { id: 'sign-up.password.label', defaultMessage: 'Password' },
  passwordPlaceholder: {
    id: 'sign-up.password.placeholder',
    defaultMessage: 'Make it a good one',
  },
  returnAddressLabel: {
    id: 'sign-up.returnAddress.label',
    defaultMessage: 'Your Ethereum Return Address',
  },
  returnAddressPlaceholder: {
    id: 'sign-up.returnAddress.placeholder',
    defaultMessage: 'Your address',
  },
  walletLabel: { id: 'sign-up.wallet.label', defaultMessage: 'Wallet' },
  walletPlaceholder: {
    id: 'sign-up.wallet.placeholder',
    defaultMessage: 'Select one...',
  },
  walletHint: {
    id: 'sign-up.wallet.hint',
    defaultMessage:
      'Make sure that it is an ERC20 token compatible wallet and not an exchange address such as Coinbase',
  },
  termsAndConditions: {
    id: 'sign-up.terms-and-conditions',
    defaultMessage:
      'By clicking register, you agree to our Terms and Conditions',
  },
  submit: { id: 'sign-up.submit', defaultMessage: 'Register' },
})

class TokenSignUp extends React.Component {
  constructor(props) {
    super(props)
    this.state = { isLoadingSignup: true }
  }

  componentWillReceiveProps(nextProps) {
    const { invitation, requested, submitting, params, dispatch } = nextProps
    const hasRequestedToken = requested[`invitations/${params.token}`]
    if (hasRequestedToken && !invitation) {
      dispatch(routerActions.replace('/login'))
    }
    this.setState({
      isLoadingSignup: submitting || !hasRequestedToken || !invitation,
    })
  }

  componentWillMount() {
    const { firebase, dispatch, redeemInvitation } = this.props
    firebase.auth().onAuthStateChanged(function(user) {
      if (user) {
        redeemInvitation()
        setTimeout(() => {
          dispatch(routerActions.replace('/'))
        }, 500)
      }
    })
  }

  render() {
    const {
      handleSubmit,
      createUser,
      error,
      storefront,
      submitFailed,
      pristine,
      invalid,
      submitting,
      invitation,
    } = this.props

    const { intl } = this.context
    const termsLabel = `I have read and agree to the Terms and Conditions of the ${
      storefront.name
    } Sale`

    return (
      <UI.Form onSubmit={handleSubmit(createUser)}>
        <UI.Card
          loading={this.state.isLoadingSignup}
          image={invitation && storefront.iconUrl}
        >
          <UI.Text center size="large">
            <FormattedMessage {...messages.header} />
          </UI.Text>

          <UI.Divider hidden />

          {submitFailed && <UI.Message type="error">{error}</UI.Message>}

          <UI.Divider hidden />

          <Field
            fluid
            label={intl.formatMessage(messages.displayNameLabel)}
            name="displayName"
            component={UI.TextField}
          />

          <Field
            fluid
            disabled
            icon="user"
            iconPosition="left"
            label={intl.formatMessage(messages.emailLabel)}
            name="email"
            component={UI.TextField}
          />

          <Field
            fluid
            icon="lock"
            iconPosition="left"
            label={intl.formatMessage(messages.passwordLabel)}
            placeholder={intl.formatMessage(messages.passwordPlaceholder)}
            name="password"
            type="password"
            component={UI.TextField}
          />

          <UI.Divider />

          <Field
            fluid
            label={intl.formatMessage(messages.returnAddressLabel)}
            placeholder={intl.formatMessage(messages.returnAddressPlaceholder)}
            name="destinationAddress"
            component={UI.TextField}
          />

          <Field
            fluid
            label={intl.formatMessage(messages.walletLabel)}
            placeholder={intl.formatMessage(messages.walletPlaceholder)}
            name="wallet"
            component={UI.SelectField}
            options={walletOptions}
            hint={intl.formatMessage(messages.walletHint)}
          />

          <UI.Divider />

          <UI.Link to={storefront.termsAndConditionsLink} target="_blank">
            View the {storefront.name} Sale Terms and Conditions
          </UI.Link>

          <Field
            label={termsLabel}
            name="termsAgreement"
            component={UI.CheckField}
          />

          <UI.Link to="https://coincart.co/terms" target="_blank">
            View the CoinCart Terms of Service
          </UI.Link>

          <Field
            label="I have read and agree to the CoinCart Terms of Service"
            name="ccTermsAgreement"
            component={UI.CheckField}
          />

          <UI.Divider hidden />

          <UI.Button disabled={pristine || invalid || submitting}>
            <FormattedMessage {...messages.submit} />
          </UI.Button>
        </UI.Card>

        <UI.Text center size="small">
          <FormattedMessage {...messages.haveAnAccount} />

          <UI.Link to="/login">
            &nbsp; <FormattedMessage {...messages.signIn} />
          </UI.Link>
        </UI.Text>
      </UI.Form>
    )
  }
}

TokenSignUp.contextTypes = {
  intl: intlShape,
}

const watches = props => [{ path: `/invitations/${props.params.token}` }]

const selectors = createStructuredSelector({
  invitation: ({ firebase }, { params }) =>
    (firebase.data.invitations || {})[params.token],
  initialValues: ({ firebase }, { params, storefrontId }) => {
    const invitation = (firebase.data.invitations || {})[params.token]

    return {
      email: '',
      displayName: '',
      destinationAddress: '',
      password: '',
      wallet: '',
      storefrontId,
      ...invitation,
    }
  },
  storefront: (state, { storefront }) => storefront,
  requested: ({ firebase }) => firebase.requested,
})

const actions = (dispatch, props) => ({
  redeemInvitation: async () => {
    try {
      const authToken = await props.firebase.auth().currentUser.getIdToken(true)
      const invitationToken = props.params.token
      if (invitationToken) {
        await commands.redeemInvitation(authToken, { invitationToken })
      }
    } catch (error) {
      throw new SubmissionError({ _error: error.message })
    }
  },
  createUser: async ({ email, password, ...profile }) => {
    try {
      await commands.claimInvitation({
        email,
        password,
        invitationToken: props.params.token,
        ...profile,
      })

      dispatch({
        type: 'INVITATION_REGISTRATION',
        track: { email },
      })

      setTimeout(() => {
        props.firebase.login({ email, password })
        dispatch(routerActions.push('/'))
      }, 500)
    } catch (error) {
      throw new SubmissionError({ _error: error.message })
    }
  },
})

const validate = values => {
  const errors = {}

  if (validator.isEmpty(values.email))
    errors.email = 'E-mail address is required'
  if (!validator.isEmail(values.email))
    errors.email = 'Please enter a valid email address'
  if (
    !validator.isEmpty(values.destinationAddress) &&
    !isValidAddress(values.destinationAddress)
  )
    errors.destinationAddress = 'This is an invalid Ethereum Address'
  if (validator.isEmpty(values.password))
    errors.password = 'Password is required'
  if (!validator.isLength(values.password, { min: 8, max: undefined }))
    errors.password = 'Password must be at least 8 characters'

  return errors
}

const warn = values => {
  const warn = {}

  if (
    !validator.isEmpty(values.destinationAddress) &&
    isValidAddress(values.destinationAddress) &&
    !isValidChecksumAddress(values.destinationAddress)
  )
    warn.destinationAddress =
      'This is an invalid (EIP-55) Ethereum Address - please double-check'

  return warn
}

export default compose(
  firebaseConnect(watches),
  connect(selectors, actions),
  reduxForm({ form: 'TokenSignUp', validate, warn, enableReinitialize: true })
)(TokenSignUp)



// WEBPACK FOOTER //
// ./src/screens/TokenSignUp.js